import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Avatar, Box, Chip, CircularProgress, Dialog, InputAdornment,
  Stack, TextField, Typography,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import { useNavigate } from 'react-router-dom'
import type { SearchResult } from '@cmms/api-client'
import { api } from '../lib/api'

const MODULE_META: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  work_order: { icon: BuildOutlinedIcon, color: '#3B82F6', label: 'Work Order' },
  asset:      { icon: CategoryOutlinedIcon, color: '#8B5CF6', label: 'Asset' },
  location:   { icon: LocationOnOutlinedIcon, color: '#0D9488', label: 'Location' },
  part:       { icon: Inventory2OutlinedIcon, color: '#D97706', label: 'Part' },
  vendor:     { icon: StorefrontOutlinedIcon, color: '#059669', label: 'Vendor' },
  user:       { icon: PersonOutlinedIcon, color: '#DB2777', label: 'Person' },
  site:       { icon: WarehouseOutlinedIcon, color: '#0E7490', label: 'Site' },
  team:       { icon: GroupsOutlinedIcon, color: '#7C3AED', label: 'Team' },
}

interface Props {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults([]); return }
    setLoading(true)
    try {
      const data = await api.analytics.search(q)
      setResults(Array.isArray(data) ? data : [])
      setActiveIndex(0)
    } catch { setResults([]) }
    finally { setLoading(false) }
  }, [])

  function handleChange(val: string) {
    setQuery(val)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => doSearch(val), 250)
  }

  function handleSelect(result: SearchResult) {
    onClose()
    navigate(result.route)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault()
      handleSelect(results[activeIndex])
    }
  }

  // Group results by module
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    ;(acc[r.module] ??= []).push(r)
    return acc
  }, {})

  let flatIndex = -1

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '15vh' } }}
      PaperProps={{
        sx: {
          borderRadius: 3.5, overflow: 'hidden', width: 620, maxWidth: '90vw',
          bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(255,255,255,0.92)' : 'rgba(30,41,59,0.92)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid',
          borderColor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
          boxShadow: (t) => t.palette.mode === 'light'
            ? '0 24px 80px -12px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.05)'
            : '0 24px 80px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.18)' : 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)' } } }}>

      {/* Search input */}
      <Box sx={{ px: 3, py: 2.25 }}>
        <TextField
          inputRef={inputRef}
          placeholder="Search across your workspace..."
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 22, color: 'primary.main', opacity: 0.7 }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
            ) : (
              <Chip label="ESC" size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary', borderRadius: 1 }} />
            ),
            sx: { fontSize: 16, fontWeight: 500, letterSpacing: '-0.01em' },
          }}
        />
      </Box>

      <Box sx={{ mx: 2.5, borderBottom: '1px solid', borderColor: 'divider' }} />

      {/* Results */}
      <Box sx={{ maxHeight: 380, overflowY: 'auto', scrollbarWidth: 'thin', py: 1 }}>
        {query.trim().length < 2 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <SearchOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1.5, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Search your workspace
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
              Find work orders, assets, people, and more
            </Typography>
          </Box>
        ) : !loading && results.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
              No results found
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
              Try a different search term
            </Typography>
          </Box>
        ) : (
          Object.entries(grouped).map(([module, items]) => {
            const meta = MODULE_META[module] ?? { icon: SearchOutlinedIcon, color: '#64748B', label: module }
            const Icon = meta.icon
            return (
              <Box key={module} sx={{ mb: 0.5 }}>
                <Typography sx={{ px: 3, py: 0.75, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.disabled' }}>
                  {meta.label}s
                </Typography>
                {items.map((r) => {
                  flatIndex++
                  const idx = flatIndex
                  const isActive = idx === activeIndex
                  const subtitle = Object.values(r.meta ?? {}).filter(Boolean).join(' · ')
                  return (
                    <Box
                      key={`${r.module}-${r.id}`}
                      onClick={() => handleSelect(r)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      sx={{
                        mx: 1.5, px: 2, py: 1.25, cursor: 'pointer', borderRadius: 2,
                        bgcolor: isActive ? (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)' : 'transparent',
                        transition: 'background-color 60ms ease',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.75}>
                        <Box sx={{
                          width: 34, height: 34, borderRadius: 2, flexShrink: 0,
                          bgcolor: `${meta.color}12`, color: meta.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon sx={{ fontSize: 17 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.005em' }} noWrap>
                            {r.name}
                          </Typography>
                          {subtitle && (
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                              {subtitle}
                            </Typography>
                          )}
                        </Box>
                        {isActive && (
                          <Chip label="↵" size="small" sx={{ height: 20, minWidth: 28, fontSize: 11, fontWeight: 700,
                            bgcolor: 'action.hover', color: 'text.secondary', borderRadius: 1,
                            '& .MuiChip-label': { px: 0.5 } }} />
                        )}
                      </Stack>
                    </Box>
                  )
                })}
              </Box>
            )
          })
        )}
      </Box>

      {/* Footer */}
      {results.length > 0 && (
        <Box sx={{ px: 3, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2.5} justifyContent="center">
            {[
              { key: '↑↓', label: 'Navigate' },
              { key: '↵', label: 'Open' },
              { key: 'esc', label: 'Dismiss' },
            ].map((h) => (
              <Stack key={h.key} direction="row" alignItems="center" spacing={0.5}>
                <Chip label={h.key} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: 'action.hover', color: 'text.disabled', borderRadius: 0.75, '& .MuiChip-label': { px: 0.75 } }} />
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>{h.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Dialog>
  )
}
